import { HEADERS } from './fixtures';
import { RiskAgentPicker } from 'views/pickers';
import { config } from 'config';
import { useTranslation } from 'react-i18next';
import { validationSchema } from 'shared/utils';
import {
    ButtonFooter,
    ExpandButton,
    FormGroup,
    Input,
    Modal,
    Table,
    TagList
} from 'mol-ui';
import {
    Container,
    InputContainer,
    TableContainer
} from './styled';
import React, { useEffect, useState } from 'react';
import {
    optionActions,
    parseRiskAgent
} from 'core/Option';
import {
    useArray,
    useDispatch,
    useErrors,
    useObjectState,
    useSelector
} from 'hooks';
const { riskAgents } = config;

const DEFAULT_POSITION = {
    name: ''
};

const PositionListing = () => {

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { clearError, clearErrors, errors, setErrors } = useErrors();
    const [selectedRiskAgent, setSelectedRiskAgent] = useState(null);
    const [selectedPosition, setSelectedPosition, setValue] = useObjectState(null);
    const [currentRiskAgents, setCurrentRiskAgents, {
        find: currentRiskAgentsFind,
        push: currentRiskAgentsPush,
        remove: currentRiskAgentsRemove
    }] = useArray([], { type: 'object' });

    const {
        positions,
        positionsError,
        positionsLoading,
        positionsStatus
    } = useSelector('option');

    useEffect(() => {
        setSelectedPosition(null);
    }, [positions, setSelectedPosition]);

    useEffect(() => {
        dispatch(optionActions.getPositions());
    }, []);

    useEffect(() => {
        if (!selectedPosition?.id) return;
        const { risk_agents } = selectedPosition;
        setCurrentRiskAgents(risk_agents.map(riskAgent => parseRiskAgent(riskAgent)));
    }, [selectedPosition]);

    const actionController = [{
        icon: 'edit',
        onClick: setSelectedPosition,
        title: t('Editar')
    }];

    const handleAddRiskAgent = () => {
        currentRiskAgentsPush(selectedRiskAgent);
        setSelectedRiskAgent(null);
    };

    const handleRemoveRiskAgent = ({ id }) => {
        currentRiskAgentsRemove('id', id);
    };

    const handleChange = (id, value) => {
        clearError(id);
        if (!!positionsError[id]) {
            const newErrors = { ...positionsError };
            delete newErrors[id];
            dispatch(optionActions.setOptionPositionsError(newErrors));
        }
        setValue(id, value);
    };

    const handleNewPosition = () => {
        setCurrentRiskAgents([]);
        dispatch(optionActions.setOptionPositionsError({}));
        clearErrors();
        setSelectedPosition({ ...DEFAULT_POSITION });
    };

    const onSubmit = () => {
        if (!validate()) return;
        const { risk_agents, ...data } = selectedPosition;
        dispatch(optionActions.upsertPosition({
            ...data,
            ...(riskAgents && { risk_agent_ids: currentRiskAgents.map(({ id }) => id) })
        }));
    };

    const validate = () => {
        const { errors, isValid } = validationSchema({
            name: [!!selectedPosition?.name, t('Requerido')]
        });
        setErrors(errors);
        return isValid;
    };

    return (
        <Container>
            <Modal active={ !!selectedPosition }>
                <FormGroup label={ t('Nombre') } width="300px">
                    <Input
                        error={ errors?.name ?? positionsError?.name }
                        value={ selectedPosition?.name }
                        width="100%"
                        onChange={ value => handleChange('name', value) }
                    />
                </FormGroup>
                {
                    riskAgents &&
                    <>
                        <FormGroup label={ t('Agentes de riesgo') } width="100%">
                            <InputContainer>
                                <RiskAgentPicker
                                    objectChange
                                    filter={ ({ id }) => !currentRiskAgentsFind('id', id) }
                                    margin="0px 10px 0px 0px"
                                    value={ selectedRiskAgent?.id }
                                    width="250px"
                                    onChange={ setSelectedRiskAgent }
                                />
                                <ExpandButton
                                    circle
                                    disabled={ !selectedRiskAgent }
                                    icon="add"
                                    size={ 40 }
                                    onClick={ handleAddRiskAgent }
                                />
                            </InputContainer>
                        </FormGroup>
                        <TagList
                            direction="column"
                            items={ currentRiskAgents }
                            margin="20px 0px 0px 0px"
                            onRemove={ handleRemoveRiskAgent }
                        />
                    </>
                }
                <ButtonFooter margin="20px 0px 0px 0px">
                    <ExpandButton
                        danger
                        caption={ t('Cancelar') }
                        disabled={ positionsStatus === 'loading' }
                        size={ 80 }
                        onClick={ () => setSelectedPosition(null) }
                    />
                    <ExpandButton
                        caption={ selectedPosition?.id ? t('Editar') : t('Crear') }
                        loading={ positionsStatus === 'loading' }
                        size={ 80 }
                        onClick={ onSubmit }
                    />
                </ButtonFooter>
            </Modal>
            <TableContainer>
                <Table
                    config={ HEADERS }
                    data={ positions }
                    loading={ positionsLoading }
                    rowActions={ actionController }
                />
            </TableContainer>
            <ButtonFooter>
                <ExpandButton
                    caption={ t('Nuevo cargo') }
                    icon="add"
                    size={ 100 }
                    width={ 200 }
                    onClick={ handleNewPosition }
                />
            </ButtonFooter>
        </Container>
    );
};

export default PositionListing;
